import { ApolloClient, ApolloLink, CombinedGraphQLErrors, HttpLink, InMemoryCache, ServerError } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';

import { CONFIG } from 'src/config';
import { DigiformaApiError } from 'src/errors/digiforma-api-error';
import { instrumentation } from 'src/instrumentation/instrumentation';
import { getToken } from 'src/user';

const httpLink = new HttpLink({
  uri: `${CONFIG.serverRoot}/api/v1/graphql`,
});

const authLink = new SetContextLink(({ headers }) => {
  const token = getToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

function createErrorLink({ onNetworkError }) {
  return new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, path, locations, code, status_code }) => {
        const apiError = new DigiformaApiError(message, { cause: error });
        console.log(`[GraphQL error]: ${message}`, { locations, path, operation, error: apiError });
        if (error.code === 'unauthorized' || error.status_code === 403) return;
        if (code === 'unauthorized' || status_code === 403) return;
        if (code === 'not_found' || status_code === 404) return;

        instrumentation.captureException(apiError, {
          extra: {
            operationName: operation.operationName,
          },
        });
      });
    } else if (ServerError.is(error)) {
      console.log(`[Network error]: ${error}`);
      onNetworkError?.(error);
    } else if (error) {
      console.log(`[Unknown error]: ${error}`);
    }
  });
}

export const createApolloClient = ({ onNetworkError }) => {
  const link = ApolloLink.from([createErrorLink({ onNetworkError }), authLink, httpLink]);
  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
    connectToDevTools: true,
    dataIdFromObject: object => `${object.__typename}:${object.id}`,
    defaultOptions: {
      // The useQuery hook uses Apollo Client's watchQuery function
      // see https://www.apollographql.com/docs/react/api/core/ApolloClient/#example-defaultoptions-object
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        // see https://www.apollographql.com/docs/react/migrating/apollo-client-4-migration#new-notifyonnetworkstatuschange-default-value-1
        notifyOnNetworkStatusChange: false,
      },
      query: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};
